import { useCallback, useState, type RefObject } from 'react';

/**
 * Position + open-state for a dropdown rendered into a `document.body` portal,
 * anchored just below an element. Portaling escapes ancestor `overflow` /
 * stacking contexts (e.g. a scrollable dialog body or its action bar) that
 * would otherwise clip or paint over an inline `absolute` dropdown.
 *
 * `strategy` must match how the anchor itself is positioned:
 * - `'absolute'` (default): anchor scrolls with the page. Coordinates include
 *   the scroll offset; render the portal with `position: absolute`.
 * - `'fixed'`: anchor is viewport-fixed (e.g. a centered modal). Coordinates are
 *   viewport-relative with no scroll offset; render with `position: fixed` so a
 *   scrolled page behind the modal doesn't push the dropdown down.
 *
 * Render the returned `pos` with the returned `position` and a high `zIndex`.
 */
export function usePortalDropdown(
  anchorRef: RefObject<HTMLElement | null>,
  strategy: 'absolute' | 'fixed' = 'absolute',
) {
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [open, setOpen] = useState(false);

  const openDropdown = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const scrollY = strategy === 'fixed' ? 0 : window.scrollY;
    const scrollX = strategy === 'fixed' ? 0 : window.scrollX;
    setPos({ top: rect.bottom + scrollY + 2, left: rect.left + scrollX, width: rect.width });
    setOpen(true);
  }, [anchorRef, strategy]);

  const closeDropdown = useCallback(() => setOpen(false), []);

  return { pos, open, openDropdown, closeDropdown, position: strategy };
}
